// novem atom — label/value detail row (NovemHR-ekranlar port).
//
// A2 (employee-core.jsx özlük drawer + profile) and B3 (license detail
// identity strip) both rendered the same "muted label on the left, value
// on the right, hairline divider between rows" pattern. This promotes
// employee-list-content's FieldRow with two widenings the call sites
// needed: the value is a ReactNode (license detail puts a Badge / Money
// there, not just a string) and an optional `mono` for code-ish values
// (ids, emails).
//
// Layout: a flex row with a top hairline border (collapsed on the first
// row via :first-child in the skin) — so a stack of FieldRows reads as a
// definition list without per-row border wiring.
//
// Accessibility: emits a <div> pair (label + value) rather than dt/dd so
// it can be dropped anywhere without requiring a <dl> ancestor. Reading
// order is label→value. Hosts that want true definition-list semantics
// can wrap their own <dl> and pass as needed (kept simple per the call
// sites, which used plain divs).

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface FieldRowProps extends HTMLAttributes<HTMLDivElement> {
  /** Muted label (left). */
  readonly label: ReactNode;
  /** Value (right). String or element (Badge / Money / link). */
  readonly value: ReactNode;
  /** Render the value in the mono family (ids, emails, hashes). */
  readonly mono?: boolean;
}

export const FieldRow = forwardRef<HTMLDivElement, FieldRowProps>(
  ({ label, value, mono = false, className, ...rest }, ref) => (
    <div ref={ref} data-component="field-row" className={cx("nai-field-row", className)} {...rest}>
      <span className="nai-field-row-label">{label}</span>
      <span className="nai-field-row-value" data-mono={mono || undefined}>
        {value}
      </span>
    </div>
  ),
);
FieldRow.displayName = "FieldRow";
